/* eslint-disable camelcase */
import * as Yup from 'yup';
import { http, authHttp } from 'lib/services/http';
import { platformAdminBaseUrl } from 'constants/apiUrls';
import { yupErrorHandler } from 'api/apiUtils';

const LICENSES_API_PATH = `${platformAdminBaseUrl}/licenses`;
const USER_LICENSES_PATH = `${platformAdminBaseUrl}/licenses`;

/**
 * Licenses Service
 * swagger: https://legacy-internal.dev.nextiva.xyz/license_service/swagger-ui.html
 * swagger auth: LabLicenseService
 * Endpoints pass through platform-admin-server
 */

export const getLicenseList = (authV3Enabled: boolean, corpAcctNbr: number) => {
  return authV3Enabled
    ? authHttp.get(`/corp-account/${corpAcctNbr}/licenses`)
    : http.get(`${LICENSES_API_PATH}/list`);
};

// GET profile ids from a license
export const getProfileIdsLicense = (
  licenseId: number,
  authV3Enabled: boolean,
  corpAcctNbr: number,
) => {
  return authV3Enabled
    ? authHttp.get(`/corp-account/${corpAcctNbr}/licenses/${licenseId}/users`)
    : http.get(`${LICENSES_API_PATH}/${licenseId}/profiles`);
};

// Bulk add and remove a license from profileIds
export const bulkUpdateLicenseAssignment = (
  licenseId: number,
  requestBody,
  authV3Enabled: boolean,
  corpAcctNbr: number,
) => {
  if (authV3Enabled) {
    const payload = { assignments: requestBody };
    return authHttp.post(
      `/corp-account/${corpAcctNbr}/licenses/${licenseId}/update-assignments`,
      payload,
    );
  }

  return http.post(
    `${LICENSES_API_PATH}/${licenseId}/updateAssignments`,
    requestBody,
  );
};

const unassignUserLicensesModel = Yup.object().shape({
  licenses: Yup.array()
    .of(
      Yup.object().shape({
        expiration_date: Yup.string(),
        license_id: Yup.number().positive().integer().required(),
      }),
    )
    .min(1)
    .required(),
  profile_id: Yup.number().positive().integer().required(),
});

const unassignUserLicensesModel2 = Yup.array()
  .of(Yup.number())
  .min(1)
  .required();

export const unassignUserLicenses = (
  body,
  userUuid: string,
  authV3Enabled: boolean,
  corpAcctNbr: number,
) => {
  if (authV3Enabled) {
    return unassignUserLicensesModel2.validate(body).then(() => {
      return authHttp.post(
        `/corp-account/${corpAcctNbr}/users/${userUuid}/licenses/unassign`,
        body,
      );
    });
  }

  return unassignUserLicensesModel
    .validate(body)
    .then(() => {
      return http.post(`${LICENSES_API_PATH}/bulk/unassign`, body);
    })
    .catch((err) => {
      throw yupErrorHandler(err.errors);
    });
};

const assignUserLicensesModel = Yup.object().shape({
  licenses: Yup.array()
    .of(
      Yup.object().shape({
        expiration_date: Yup.string(),
        license_id: Yup.number().positive().integer().required(),
      }),
    )
    .min(1)
    .required(),
  profile_id: Yup.number().positive().integer().required(),
});

const assignUserLicensesIAM = Yup.array().of(Yup.number().integer()).min(1);

export const assignUserLicenses = (
  body,
  authV3Enabled: boolean,
  corpAcctNbr: number,
  uuid: string,
) => {
  if (authV3Enabled) {
    return assignUserLicensesIAM.validate(body).then(() => {
      return authHttp.post(
        `/corp-account/${corpAcctNbr}/users/${uuid}/licenses/assign`,
        body,
      );
    });
  }
  return assignUserLicensesModel
    .validate(body)
    .then(() => {
      return http.post(`${USER_LICENSES_PATH}/bulk/assign`, body);
    })
    .catch((err) => {
      throw yupErrorHandler(err.errors);
    });
};

export const getLicensesByUsers = (corpAcctNbr: number, uuids: string) => {
  return authHttp.get(`/corp-account/${corpAcctNbr}/users/licenses`, {
    params: { uuids },
  });
};
